import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import NewPage from "../../../layouts/page/NewPage";
import Form from "./Form";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { successAlert, warnAlert } from "../../../components/alert/defaultAlert";
import axiosInstance from "../../../../services/AxiosInstance";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";

/** "10:00 Carlos Lima com Ana Souza (Centro)", para o aviso dos agendamentos afetados. */
const describeAppointment = (appointment, showBranch) => `${String(appointment.scheduledDate || '').slice(11, 16)} ${appointment.customerName || 'Cliente'}`
    + `${appointment.employeeName ? ` com ${appointment.employeeName}` : ''}`
    + `${showBranch && appointment.branchName ? ` (${appointment.branchName})` : ''}`;

const SpecialDayNew = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [entityData, setEntityData] = useState(null);
    const [branchOptions, setBranchOptions] = useState([]);

    // Hoje congelado no estado, para o Formik não reinicializar o formulário a cada render.
    const [today] = useState(() => new Date());

    const getInitialValues = () => ({
        specialDate: today,
        isOpen: false,
        openTime: '',
        closeTime: '',
        breakStartTime: '',
        breakEndTime: '',
        reason: 'Feriado',
        // A data nova vale para todas as filiais do usuário: o feriado da rede é o caso comum.
        branches: branchOptions
    });

    // As filiais do próprio usuário: operador restrito à sua unidade só fecha a agenda dela.
    useEffect(() => {
        axiosInstance.get(`${Api.main.branch}/user`)
            .then((response) => setBranchOptions((Array.isArray(response.data) ? response.data : [])
                .filter((branch) => branch.status === "ACTIVE")
                .map((branch) => ({ value: branch.id, label: branch.name }))))
            .catch(() => setBranchOptions([]));
    }, []);

    // As regras de horário (fechamento depois da abertura, intervalo dentro da faixa) ficam no
    // servidor, que devolve a mensagem certa; aqui só o que impede o envio.
    const formSchema = Yup.object().shape({
        specialDate: Yup.date().required(t('validation.required.date')).nullable(),
        reason: Yup.string().trim().required(t('validation.required.reason')).max(80),
        branches: Yup.array().min(1, t('validation.required.branch'))
    });

    const formik = useFormik({
        initialValues: getInitialValues(),
        validationSchema: formSchema,
        onSubmit: (values) => {
            const payload = {
                // A data vai como texto no fuso de quem cadastrou, para não virar o dia seguinte em UTC.
                specialDate: formatDateToYYYYMMDD(values.specialDate),
                isOpen: values.isOpen,
                openTime: values.isOpen ? values.openTime || null : null,
                closeTime: values.isOpen ? values.closeTime || null : null,
                breakStartTime: values.isOpen ? values.breakStartTime || null : null,
                breakEndTime: values.isOpen ? values.breakEndTime || null : null,
                reason: values.reason,
                branchIds: values.branches.map((option) => option.value)
            };

            const request = entityData?.id
                ? axiosInstance.patch(`${Api.appointment.special_day}/${entityData.id}`, payload)
                : axiosInstance.post(Api.appointment.special_day, payload);

            request
                .then((response) => {
                    const afterSave = () => (entityData == null ? onReset() : navigate(MenuConfig.appointment.special_day.route));
                    const affected = response.data?.affectedAppointments || [];

                    if (affected.length === 0) {
                        successAlert(t('message.operation_successfully'), afterSave);
                        return;
                    }

                    // Os agendamentos que já existiam no dia não são cancelados: a recepção
                    // decide se remarca ou avisa o cliente.
                    const showBranch = (response.data.branchIds || []).length > 1;
                    warnAlert(`Salvo. ${affected.length === 1 ? 'Há 1 agendamento' : `Há ${affected.length} agendamentos`} `
                        + `nesse dia fora do que as filiais vão atender. Remarque ou avise os clientes: `
                        + `${affected.map((appointment) => describeAppointment(appointment, showBranch)).join('; ')}.`);
                    afterSave();
                })
                .catch(error => ApiError(error))
                .finally(() => formik.setSubmitting(false));
        }
    });

    // Depois de cadastrar, o formulário volta limpo e com todas as filiais, pronto para o próximo
    // feriado: quem cadastra um costuma cadastrar os do ano de uma vez.
    const onReset = () => {
        formik.resetForm({ values: getInitialValues() });
    };

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    };

    return (
        <NewPage
            motherMenu={t('appointments')}
            childMenu={t('special_days')}
            activeMenu={entityData ? t('update') : t('new')}
            child={
                <Form
                    formik={formik}
                    entityData={entityData}
                    branchOptions={branchOptions}
                />
            }
            setEntityData={setEntityData}
            path={Api.appointment.special_day}
            isSubmitting={formik.isSubmitting}
            handleSubmit={onSubmit}
            cancelRoute={MenuConfig.appointment.special_day.route}
        />
    );
};

export default SpecialDayNew;
